import { FC, useState } from "react";
import { DocumentTypeRu, IdTableVariant } from "../../@types/enumsGlobal";
import { BaseModalWindow, IButton } from "./BaseModalWindow";
import styles from "./styles/SelectDocumentModalWindow.module.scss";
import { IRadioButtonOption, RadioGroup } from "../radioGroup/radioGroup";
import { useAppContext } from "../../system/providers/appContextProvider";
import { LotDataProvider } from "../../Services/DataProviders/LotDataProvider";
import { IEntitySimpleDTO } from "../../libs/coreapi-dto/@types/common";
import { ILotDTO } from "../../libs/coreapi-dto/accounting/lot";
import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";

import { InvoiceSelectorModal } from "../../Business/Documents/Invoice/Components/InvoiceSelectorModal";
import { MovementSelectorModal } from "../../Business/Documents/Movement/Components/MovementSelectorModal";
import { ActRevaluationSelectorModal } from "../../Business/Documents/ActRevaluation/Components/ActRevaluationSelectorModal";
import { ActDisassemblingSelectorModal } from "../../Business/Documents/ActDisassembling/Components/ActDisassemblingSelectorModal";
import { ActDisagregationSelectorModal } from "../../Business/Documents/ActDisagregation/Components/ActDisagregationSelectorModal";
import { useTranslation } from "react-i18next";
import { CreateSelectorGridFilter } from "../../system/hooks/useGridFilter";
import { InvoiceOutSelectorModal } from "../../Business/Documents/InvoiceOut/Components/InvoiceOutSelectorModal";
import { NIL as uuidNull } from "uuid";
import CashSessionSelectorModal from "../../Business/Accounting/CashSession/Components/CashSessionSelectorModal";
import { RequestForMovementCreatorModal } from "../../Business/Documents/RequestForMovement/RequestForMovementCreatorModal";
import { RequestForMovementSelectorModal } from "../../Business/Documents/RequestForMovement/RequestForMovementCreator/RequestForMovementSelectorModal";
import { boolean } from "mathjs";
import { useTabsContext } from "../../system/providers/tabsProvider";

interface IDocType {
    name: DocumentTypeRu;
}

const DefaultDocTypes: IDocType[] = [
    { name: DocumentTypeRu.invoice }, 
    { name: DocumentTypeRu.movement }, 
    { name: DocumentTypeRu.actRevaluation },
    { name: DocumentTypeRu.actDisassembling },
    { name: DocumentTypeRu.actDisagregation },
    { name: DocumentTypeRu.requestForMovement }
];

interface ISelectDocumentModalWindowProps {
    ok: (value: ILotDTO[], document: { idTable: number; idGlobal: string; displayName: string }) => void;
    cancel: IButton;
    onlyProc?: boolean
    dateDocument: DateTime
    idStoreGlobal: string
    filterExtra?: IDocumentGridFilterExtra
    content?: IDocType[]
    isSkipLotSelection?: boolean
}

interface ISelectedDoc {
    doc?: IRadioButtonOption;
    open: boolean;
}

export const SelectDocumentModalWindow: FC<ISelectDocumentModalWindowProps> = (props) => {
    const [doc, setDoc] = useState<ISelectedDoc>({ doc: undefined, open: false });
    const { t } = useTranslation();
    const baseT = (value: string) => t('modals.selectDocument.' + value);
    const [gridFilter] = useState<IGridFilter>(
    {
    numberPerPage: 15,
    pageNumber: 1,
    columnFilters: [
        {
            name: "deleted",
            operator: "Eq",
            value: false,
        },
    ]
    });
    
    return doc.doc && doc.open ? (
            <SelectorModal
                docName={doc.doc.displayName}
                close={props.cancel.onClick}
                dateDocument={props.dateDocument}
                idStoreGlobal={props.idStoreGlobal}
                filterExtra={props.filterExtra}
                gridFilter={gridFilter}
                isSkipLotSelection={props.isSkipLotSelection}
                ok={(value, document) => {
                    props.ok(value, document);
                    props.cancel.onClick();
                }}
                onlyProc={props.onlyProc}
            />
    ) : (
            <BaseModalWindow
                modalWindowClassName={styles.modalWindow}
                header={baseT("header")}
                ok={{ onClick: () => setDoc({ ...doc, open: true }) }}
                cancel={props.cancel}
            >
                <ModalContent
                    doc={doc.doc}
                    setDoc={(value) => setDoc({ ...doc, doc: value })}
                    content={props.content ?? DefaultDocTypes}
                />
            </BaseModalWindow>
    );
};

interface IModalContentProps {
    content: IDocType[];
    doc: IRadioButtonOption | undefined;
    setDoc: (value: IRadioButtonOption | undefined) => void;
}

const ModalContent: FC<IModalContentProps> = (props) => {

    const convertNames = (content: IDocType[]) => {
        return content.map(x => ({ displayName: x.name, value: x.name }))
    };

    return (
        <RadioGroup
            value={props.doc}
            options={convertNames(props.content)}
            onSelect={(value) => {
                props.setDoc(value);
            }}
            vertical
        />
    );
};

interface ISelectorModalProps {
    docName: string;
    close: () => void;
    ok: (value: ILotDTO[], document: { idTable: number; idGlobal: string; displayName: string }) => void;
    onlyProc?: boolean
    dateDocument: DateTime
    idStoreGlobal: string
    filterExtra? : IDocumentGridFilterExtra
    gridFilter: IGridFilter
    isSkipLotSelection?: boolean
}

const SelectorModal: FC<ISelectorModalProps> = (props) => {

    const tabsContext = useTabsContext();
    const appContext = useAppContext();
    const lotDP = new LotDataProvider(appContext.coreApiService);
    
    const getIdTable = (docName : string): IdTableVariant => {
        switch (docName) {
            case DocumentTypeRu.invoice:
                return IdTableVariant.Invoice;                 
            case DocumentTypeRu.movement:
                return IdTableVariant.Movement;  
            case DocumentTypeRu.actRevaluation:
                return IdTableVariant.ActRevaluation2;  
            case DocumentTypeRu.actDisassembling:
                return IdTableVariant.ActDisassembling;  
            case DocumentTypeRu.actDisagregation:
                return IdTableVariant.ActDisagregation;  
            case DocumentTypeRu.requestForMovement:
                return IdTableVariant.RequestForMovement;
            case DocumentTypeRu.invoiceOut:
                return IdTableVariant.InvoiceOut;
            case DocumentTypeRu.cashSession:
                return IdTableVariant.CashSession;
        }
        return 0 as IdTableVariant;
    };

    const getGridFiltersExtra = (filterExtra: IDocumentGridFilterExtra | undefined): IGridColumnFilter[] => {
        let gridColumnFilterArray = [] as IGridColumnFilter[];
        if (filterExtra) {
            if (props.idStoreGlobal && props.idStoreGlobal !== uuidNull) {
                let columnFilter = { name: "idStoreGlobalTo", value: props.idStoreGlobal, operator: 'Eq' } as IGridColumnFilter
                gridColumnFilterArray.push(columnFilter);
            }
            else if (filterExtra.isFilterByIdStoreGlobalActive && filterExtra.idStoreGlobalTo) {
                let columnFilter = { name: "idStoreGlobalTo", value: filterExtra.idStoreGlobalTo, operator: 'Eq' } as IGridColumnFilter
                gridColumnFilterArray.push(columnFilter);
            }

            if (filterExtra.idContractorToGlobal) {
                let columnFilter = { name: "idContractorToGlobal", value: filterExtra.idContractorToGlobal, operator: 'Eq' } as IGridColumnFilter
                gridColumnFilterArray.push(columnFilter);
            }
        }
        else if (props.idStoreGlobal && props.idStoreGlobal !== uuidNull) {
                let columnFilter = { name: "idStoreGlobalTo", value: props.idStoreGlobal, operator: 'Eq' } as IGridColumnFilter
                gridColumnFilterArray.push(columnFilter);
        }

        return gridColumnFilterArray;         
    }

    const [isRequestModalOpen, setIsRequestModalOpen] = useState(true);

    const handleCreateMovement = (value: IEntitySimpleDTO) => {
        if (!value.idGlobal) return;

        tabsContext.closeCurrent();

        // Генерируем новый idGlobal для документа перемещения
        const movementIdGlobal = uuidv4();

        // Открываем MovementCreationView как табу
        tabsContext.openChild(
            'movement_creation_view',
            'create',
            movementIdGlobal,
            () => {},
            {
                requestId: value.idGlobal,
                mnemocode: value.displayName,
                isSkipLotSelection: props.isSkipLotSelection
            }
        );

        setIsRequestModalOpen(false);
        props.close();
    };

    const gridProps = {
        ok: (value: IEntitySimpleDTO) => {
            let idTable = getIdTable(props.docName);
            if (idTable === IdTableVariant.RequestForMovement) {
                handleCreateMovement(value)
                setIsRequestModalOpen(false);
            } else
            if (!props.filterExtra || props.filterExtra.isGetLotByDocumentActive) {
                lotDP.getByDocument(value.idGlobal, (e) => {
                    props.ok(e, {
                        idTable: idTable,
                        idGlobal: value.idGlobal,
                        displayName: value.displayName,
                    });
                })
            }
            else
            {
                let lots = {} as ILotDTO[];
                props.ok(lots, {
                    idTable: idTable,
                    idGlobal: value.idGlobal,
                    displayName: value.displayName,
                });
            }
        },
        cancel: () => {
            setIsRequestModalOpen(false);
            props.close();
        },
        gridFilter: props.gridFilter,
        gridFiltersExtra: getGridFiltersExtra(props.filterExtra)
    } as IGridSelectorModalProps;
    
    switch (props.docName) {
        case DocumentTypeRu.invoice:
            return (
                <InvoiceSelectorModal
                    showToolbar
                    gridProps={gridProps}
                    dateDocument={props.dateDocument}
                    idStoreGlobal={props.idStoreGlobal}
                />
            );
        case DocumentTypeRu.movement:
            return (
                <MovementSelectorModal
                    showToolbar
                    gridProps={gridProps}
                    dateDocument={props.dateDocument}
                    idStoreGlobal={props.idStoreGlobal}
                />
            );
        case DocumentTypeRu.actRevaluation:
            return (
                <ActRevaluationSelectorModal
                    showToolbar
                    gridProps={gridProps}
                    dateDocument={props.dateDocument}
                    idStoreGlobal={props.idStoreGlobal}
                />
            );
        case DocumentTypeRu.actDisassembling:
            return (
                <ActDisassemblingSelectorModal
                    showToolbar
                    gridProps={gridProps}
                    dateDocument={props.dateDocument}
                    idStoreGlobal={props.idStoreGlobal}
                />
            );
        case DocumentTypeRu.actDisagregation:
            return (
                <ActDisagregationSelectorModal
                    showToolbar
                    gridProps={gridProps}
                    dateDocument={props.dateDocument}
                    idStoreGlobal={props.idStoreGlobal}
                />
            );
        // case DocumentTypeRu.requestForMovement:
        //     return (
        //         <RequestForMovementSelectorModal
        //             showToolbar
        //             gridProps={gridProps}
        //             dateDocument={props.dateDocument}
        //             idStoreGlobal={props.idStoreGlobal}
        //         />
        //     );
        case DocumentTypeRu.invoiceOut:
                return (
                    <InvoiceOutSelectorModal
                        showToolbar
                        gridProps={gridProps}
                        dateDocument={props.dateDocument}
                        idStoreGlobal={props.idStoreGlobal}
                    />
                );
        case DocumentTypeRu.cashSession:
            let gridFilterExtraCashSession = getGridFiltersExtra(props.filterExtra);
            if (gridFilterExtraCashSession) {
                props.gridFilter.columnFilters.push(...gridFilterExtraCashSession);
            }
                return (
                    <CashSessionSelectorModal
                        ok ={ (entity) => {gridProps.ok(entity)}}
                        cancel={() => {gridProps.cancel()}}
                    />
                );
        case DocumentTypeRu.requestForMovement:
            return (
                <RequestForMovementCreatorModal
                    showToolbar
                    gridProps={gridProps}
                    dateDocument={props.dateDocument}
                    idStoreGlobal={props.idStoreGlobal}
                />
            );
        default:
            return <></>;
    }
};
